import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import { BASE_URL } from "../../config";
import { ArrowLeftIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { useTable, usePagination } from "react-table";

const RvuHistory = () => {
  const navigate = useNavigate();
  const { rvuCode } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchHistory = async (pageIndex, pageSize) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/rvu/${rvuCode}/history`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
        params: {
          limit_offset: pageIndex * pageSize,
          limit: pageSize,
        },
      });
      setData(response.data.data.history);
      setTotalCount(response.data.total_count);
    } catch (error) {
      toast.error("Failed to fetch RVU history.", { autoClose: 3000 });
      setTimeout(() => navigate("/rvu"), 2000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory(currentPage - 1, itemsPerPage);
  }, [rvuCode, currentPage, itemsPerPage]);

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    return data.filter((log) =>
      [
        log.action,
        log.username,
        `${log.first_name || ''} ${log.last_name || ''}`,
        Object.values(log.details).join(' '),
      ]
        .join(' ')
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [data, searchQuery]);

  const columns = useMemo(
    () => [
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Details",
        accessor: "details",
        Cell: ({ value }) => (
          <span className="text-gray-600">
            RVU Category: {value.rvu_category || 'N/A'}, 
            RVU Code: {value.rvu_code || 'N/A'}, 
            Description: {value.rvu_description || 'N/A'}, 
            Value: {value.value ? value.value : 'N/A'}, 
            Status: {value.status || 'N/A'}, 
            Remarks: {value.remarks || 'N/A'},
          </span>
        ),
      },
      {
        Header: "User",
        accessor: "username",
        Cell: ({ row }) => (
          <span>{`${row.original.first_name || ''} ${row.original.last_name || ''} (${row.original.username})`}</span>
        ),
      },
      {
        Header: "Timestamp",
        accessor: "action_timestamp",
        Cell: ({ value }) => (
          <span>{new Date(value).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</span>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: 0, pageSize: itemsPerPage },
      manualPagination: true,
      pageCount: Math.ceil(totalCount / itemsPerPage),
    },
    usePagination
  );

  useEffect(() => {
    setCurrentPage(pageIndex + 1);
    fetchHistory(pageIndex, pageSize);
  }, [pageIndex, pageSize]);

  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(1, pageIndex + 1 - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(Math.ceil(totalCount / pageSize), startPage + maxPagesToShow - 1);
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-2 px-2 sm:px-3 lg:px-4">
      <ToastContainer position="top-right" autoClose={3000} />
      <motion.div
        initial={{ opacity: 6833, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-1xl mx-auto"
      >
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-xl font-semibold text-gray-900">
            Trail Log: RVU Code History: {rvuCode}
          </h1>
          <button
            type="button"
            onClick={() => navigate("/rvu")}
            className="inline-flex items-center justify-center gap-2 self-start rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 sm:self-auto"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Back to RVU Codes</span>
          </button>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="mb-4 relative">
            <input
              type="text"
              placeholder="Search history by action, user, or details..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                gotoPage(0);
              }}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm w-full shadow-sm"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">RVU History</h2>
          <div className="overflow-x-auto border border-gray-200 rounded-md">
            <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps()}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-3 text-center text-sm text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : page.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-3 text-center text-sm text-gray-500">
                      {searchQuery ? "No matching history found." : "No history found."}
                    </td>
                  </tr>
                ) : (
                  page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} className="hover:bg-gray-50">
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            className="px-4 py-3 whitespace-nowrap text-sm text-gray-900"
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-3 px-4 py-2">
            <div className="text-sm text-gray-600">
              Showing {pageIndex * pageSize + 1} to {Math.min((pageIndex + 1) * pageSize, totalCount)} of {totalCount.toLocaleString()} entries
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
                className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm ${
                  !canPreviousPage ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <ChevronDoubleLeftIcon className="h-5 w-5" />
                <span>First</span>
              </button>
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm ${
                  !canPreviousPage ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <ChevronLeftIcon className="h-5 w-5" />
                <span>Previous</span>
              </button>
              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  onClick={() => gotoPage(page - 1)}
                  className={`px-3 py-1 rounded-md text-sm ${
                    pageIndex + 1 === page ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => nextPage()}
                disabled={!canNextPage}
                className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm ${
                  !canNextPage ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span>Next</span>
                <ChevronRightIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => gotoPage(Math.ceil(totalCount / pageSize) - 1)}
                disabled={!canNextPage}
                className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm ${
                  !canNextPage ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span>Last</span>
                <ChevronDoubleRightIcon className="h-5 w-5" />
              </button>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  gotoPage(0);
                }}
                className="border border-gray-200 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size} per page
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RvuHistory;
